/**
 * LinkField — `.link-field` from the prototype (cms-redesign-mockup.html
 * ~4660–4680). A read-only URL display with an inline Copy button.
 *
 *   <LinkField value="https://thetappingsolution.app.link/session/8af12e" />
 *
 * Behavior:
 *   • Read-only by default; the input value is the URL to display
 *   • Copy uses navigator.clipboard if available, falls back to a
 *     synthetic exec-command path for older browsers
 *   • Brief "Copied!" affordance on the button (2s) so the user sees
 *     the action landed without firing a Swal/toast
 *
 * Visual recipe:
 *   • Cream wrapper with sand border, asymmetric padding (more on
 *     the left so the URL has breathing room from the edge)
 *   • Input is bare (transparent bg, no border, mono 12)
 *   • Copy button: 28px tall, surface bg, sand border, uppercase
 *     Asap Condensed label, hover tints teal
 */
import React, { useRef, useState } from 'react';
import { cn } from '../cn';

export default function LinkField({ value, className, inputId }) {
  const inputRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = value ?? '';
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else if (inputRef.current) {
        // Fallback for browsers that don't expose the async clipboard
        // API (or refuse it on insecure origins). Select the input's
        // text and rely on the deprecated `execCommand('copy')` —
        // still works in Safari and old Chromium.
        inputRef.current.select();
        document.execCommand('copy');
        inputRef.current.blur();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ignore — the user can still select + copy manually.
    }
  };

  return (
    <div
      className={cn('flex items-center', className)}
      style={{
        gap: 8,
        padding: '8px 8px 8px 14px',
        background: 'rgb(var(--color-cream))', /* cream */
        border: '1px solid rgb(var(--color-sand))', /* sand */
        borderRadius: 8,
        minWidth: 0,
      }}
    >
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        readOnly
        value={value ?? ''}
        onFocus={(e) => e.currentTarget.select()}
        style={{
          flex: 1,
          minWidth: 0,
          border: 0,
          background: 'transparent',
          outline: 'none',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 12,
          color: 'rgb(var(--color-navy))', /* text/navy */
          padding: '4px 0',
        }}
      />
      <button
        type="button"
        onClick={handleCopy}
        disabled={!value}
        aria-label="Copy link"
        className="font-condensed font-bold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          height: 28,
          padding: '0 10px',
          border: '1px solid rgb(var(--color-sand))', /* sand */
          borderRadius: 4,
          background: 'rgb(var(--color-surface))', /* surface */
          color: copied ? '#5C7E72' /* teal-green-d */ : '#9CA1A8', /* text-light */
          fontSize: 11,
          letterSpacing: '0.06em',
          transition: 'all 160ms ease',
        }}
        onMouseEnter={(e) => {
          if (e.currentTarget.disabled) return;
          e.currentTarget.style.color = '#5C7E72';
          e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
        }}
        onMouseLeave={(e) => {
          if (e.currentTarget.disabled) return;
          e.currentTarget.style.color = copied ? '#5C7E72' : '#9CA1A8';
          e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
        }}
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}
